import { cn } from "cn";
import { ChevronRight } from "lucide-react";
import type * as React from "react";

/**
 * One tappable settings row (iOS Settings; v3.2 Profile): the label over
 * the value, `placeholder` ("Not set") when the value is empty, and a
 * chevron. The whole row is the button, so it can be a dialog trigger.
 */
export function SettingsRow({
  label,
  value,
  placeholder,
  className,
  ...props
}: Omit<React.ComponentPropsWithRef<"button">, "children" | "value"> & {
  label: React.ReactNode;
  value?: React.ReactNode;
  /** Shown in the quiet colour when there is no value. */
  placeholder: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      className={cn(
        "group flex min-h-[60px] w-full items-center gap-3 px-4 py-2.5 text-left font-v3 transition-colors hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-v3-brand-ring sm:px-5",
        className,
      )}
      data-slot="settings-row"
      type="button"
      {...props}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-medium text-v3-text-3">
          {label}
        </span>
        <span
          className={
            value
              ? "mt-0.5 block truncate text-[15px] text-v3-text"
              : "mt-0.5 block text-[15px] text-v3-text-3"
          }
        >
          {value || placeholder}
        </span>
      </span>
      <ChevronRight
        aria-hidden
        className="size-4 shrink-0 text-v3-text-3 transition-colors group-hover:text-v3-text-2"
      />
    </button>
  );
}
